import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl, mxn, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar } from "@/components/Avatar";
import { Package, ArrowLeft, Recycle, ChevronRight, Mail, Send } from "lucide-react";
import { formatQuantity, unitLabel } from "@/lib/units";
import { toast } from "sonner";

const STATUS_BADGE = {
  available: "bg-[#10B981]/10 text-[#047857] border-[#10B981]/30",
  in_use: "bg-[#F59E0B]/10 text-[#92400E] border-[#F59E0B]/40",
  maintenance: "bg-[#E11D48]/10 text-[#9F1239] border-[#E11D48]/30",
  surplus: "bg-[#0052CC]/10 text-[#0052CC] border-[#0052CC]/30",
  sold: "bg-[#5C6A7D]/10 text-[#5C6A7D] border-[#5C6A7D]/30",
};

const STATUS_LABELS = { available: "Disponible", in_use: "En uso", maintenance: "Mantenimiento", surplus: "Sobrante", sold: "Vendido" };
const REGION_LABELS = { centro: "Zona Centro", norte: "Zona Norte", bajio: "Bajío", otros: "Otros estados" };

export default function AssetDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAssetAdmin = user?.role === "asset_admin";
  const [asset, setAsset] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [reuseOpen, setReuseOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [qty, setQty] = useState(1);
  const [neededUntil, setNeededUntil] = useState("");  // Sprint 19
  const [reuseMsg, setReuseMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Team dialog
  const [teamOpen, setTeamOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [notifyTarget, setNotifyTarget] = useState(null); // member or "all"
  const [notifyMsg, setNotifyMsg] = useState("");
  const [notifySending, setNotifySending] = useState(false);

  const refresh = () => api.get(`/assets/${id}`).then(({ data }) => setAsset(data));
  const loadThread = () => api.get(`/assets/${id}/reuse-thread`).then(({ data }) => setThread(data.has_thread ? data : null)).catch(() => setThread(null));
  const [thread, setThread] = useState(null);
  useEffect(() => { refresh(); loadThread(); }, [id]);

  if (!asset) return <div className="text-sm text-[#5C6A7D]">Loading…</div>;

  const maxQty = Math.max(1, parseInt(asset.quantity, 10) || 1);

  const openReuse = () => {
    setStep(1);
    setQty(1);
    setNeededUntil("");
    setReuseMsg("");
    setReuseOpen(true);
  };

  const nextStep = () => {
    const q = parseInt(qty, 10) || 0;
    if (q < 1) { toast.error("La cantidad debe ser al menos 1"); return; }
    if (q > maxQty) { toast.error(`La cantidad no puede exceder el disponible (${maxQty})`); return; }
    // Sprint 19: equipamiento provisional pasa por el paso de "fecha de necesidad" antes del mensaje.
    if (!asset.is_surplus) {
      setStep(2);
    } else {
      setStep(3);
    }
  };

  const confirmNeededUntil = () => {
    if (!neededUntil) {
      toast.error("Elige hasta cuándo lo vas a requerir.");
      return;
    }
    setStep(3);
  };

  const sendRequest = async () => {
    setSubmitting(true);
    try {
      const payload = {
        quantity: parseInt(qty, 10) || 1,
        unit: asset.unit,
        message: reuseMsg,
      };
      if (!asset.is_surplus) payload.needed_until = neededUntil;
      const { data } = await api.post(`/assets/${id}/reuse-request`, payload);
      toast.success("Solicitud enviada al equipo del proyecto.");
      setReuseOpen(false);
      if (data?.id) nav(`/reuse-requests/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falló");
    } finally {
      setSubmitting(false);
    }
  };

  const openTeam = async () => {
    if (!asset.project_id) return;
    setTeamOpen(true);
    setMembersLoading(true);
    try {
      const { data } = await api.get(`/projects/${asset.project_id}/members`);
      setMembers(data);
    } catch {
      toast.error("No fue posible cargar el equipo del proyecto");
    } finally {
      setMembersLoading(false);
    }
  };

  const openNotify = (target) => {
    setNotifyTarget(target);
    setNotifyMsg("");
  };

  const sendNotify = async () => {
    const body = notifyMsg.trim();
    if (!body) { toast.error("Escribe un mensaje"); return; }
    setNotifySending(true);
    try {
      const recipient_ids = notifyTarget === "all" ? [] : [notifyTarget.id];
      const { data } = await api.post(`/projects/${asset.project_id}/notify`, {
        recipient_ids, message: body,
      });
      toast.success(
        data.notified > 1
          ? `Mensaje enviado a ${data.notified} integrantes.`
          : "Mensaje enviado.",
      );
      setNotifyTarget(null);
      setNotifyMsg("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No fue posible enviar el mensaje");
    } finally {
      setNotifySending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="asset-detail-page">
      <Link to="/search" className="inline-flex items-center text-xs uppercase tracking-[0.18em] text-[#5C6A7D] hover:text-[#0A2540]">
        <ArrowLeft size={14} className="mr-1" /> Volver a la búsqueda
      </Link>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="aspect-[4/3] bg-[#EAF0F6] border border-[#E2E8F0] rounded-sm overflow-hidden">
            {asset.photos?.length ? (
              <img src={fileUrl(asset.photos[activePhoto])} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#5C6A7D]"><Package size={48} strokeWidth={1.5} /></div>
            )}
          </div>
          {asset.photos?.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {asset.photos.map((p, i) => (
                <button key={i} onClick={() => setActivePhoto(i)} className={`aspect-square rounded-sm overflow-hidden border ${i === activePhoto ? "border-[#0A2540]" : "border-[#E2E8F0]"}`}>
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">{asset.category}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">{asset.name}</h1>
          <div className="mt-3 flex items-center gap-2">
            {asset.is_surplus ? (
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm border bg-[#0052CC]/10 text-[#0052CC] border-[#0052CC]/30">Sobrante</span>
            ) : (
              <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm border ${STATUS_BADGE[asset.status] || ""}`}>{STATUS_LABELS[asset.status] || asset.status?.replace("_", " ")}</span>
            )}
          </div>

          <p className="mt-5 text-[#5C6A7D] leading-relaxed">{asset.description || "Sin descripción."}</p>

          <div className="mt-6 grid grid-cols-2 gap-4" data-testid="asset-fields">
            {/* Proyecto — button in asset, plain text in surplus */}
            {asset.is_surplus ? (
              <div className="border border-[#E2E8F0] rounded-sm p-4 bg-white" data-testid="field-project">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">Proyecto</div>
                <div className="font-semibold text-[#0A2540] mt-1">{asset.project_name || "—"}</div>
              </div>
            ) : (
              <button
                type="button"
                onClick={openTeam}
                className="border border-[#E2E8F0] rounded-sm p-4 bg-white text-left transition-colors hover:border-[#0A2540]/40 hover:bg-[#FAFAFA] flex items-start justify-between gap-2 group"
                data-testid="field-project-button"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">Proyecto</div>
                  <div className="font-semibold text-[#0A2540] mt-1">{asset.project_name || "—"}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#10B981] mt-1.5">Ver equipo · contactar</div>
                </div>
                <ChevronRight size={16} className="text-[#5C6A7D] mt-0.5 group-hover:text-[#0A2540] transition-colors" />
              </button>
            )}

            {/* Common fields */}
            {[
              ["Zona del proyecto", REGION_LABELS[asset.project_region] || "—"],
              ["Ubicación", asset.location || asset.project_location || "—"],
              // Asset-only: Estatus + Fecha disponible
              ...(!asset.is_surplus ? [
                ["Estatus", STATUS_LABELS[asset.status] || asset.status],
                ["Fecha disponible", asset.available_date || "—"],
              ] : []),
              // Surplus-only: Estado del material
              ...(asset.is_surplus ? [
                ["Estado del material", asset.condition || "—"],
              ] : []),
              ["Cantidad", asset.quantity],
              ["Unidad", asset.unit],
            ].map(([l, v]) => (
              <div key={l} className="border border-[#E2E8F0] rounded-sm p-4 bg-white">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">{l}</div>
                <div className="font-semibold text-[#0A2540] mt-1">{v}</div>
              </div>
            ))}
          </div>

          {asset.is_surplus && (
            <div className="mt-4 bg-[#0A2540] text-white p-6 rounded-sm" data-testid="field-internal-price">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Precio interno</div>
              <div className="font-display text-4xl font-extrabold mt-1">{mxn(asset.internal_price)}</div>
            </div>
          )}

          {/* Sprint 9: quién y cuándo cargó el activo */}
          {(asset.uploaded_by_name || asset.uploaded_at) && (
            <div className="mt-4 border border-[#E2E8F0] rounded-sm p-4 bg-white flex items-center justify-between gap-3 flex-wrap" data-testid="asset-uploaded-meta">
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">Cargado por</div>
                <div className="text-sm font-semibold text-[#0A2540]">{asset.uploaded_by_name || "—"}</div>
                {asset.uploaded_by_email && <div className="text-xs text-[#5C6A7D]">{asset.uploaded_by_email}</div>}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">Fecha de carga</div>
                <div className="text-sm font-mono text-[#0A2540]">
                  {asset.uploaded_at ? new Date(asset.uploaded_at).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
              </div>
            </div>
          )}

          {/* Sprint 11: acceso al chat de reutilización si hay solicitud activa. */}
          {thread && (
            <div className="mt-4 border border-[#10B981]/30 rounded-sm p-4 bg-[#F0FAF4] flex items-center justify-between gap-3 flex-wrap" data-testid="active-reuse-thread">
              <div className="text-sm text-[#065F46]">
                <span className="font-semibold text-[#047857]">Solicitud de reutilización activa.</span>
                <span className="text-[#065F46]/80"> Hay una conversación abierta con el equipo dueño del activo.</span>
              </div>
              <Link
                to={`/reuse-requests/${thread.request_id}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#10B981] hover:bg-[#059669] px-3 py-2 rounded-sm"
                data-testid="open-reuse-thread"
              >
                Ver conversación
              </Link>
            </div>
          )}

          {/* Primary reuse action — oculto para administrador de activos (conflicto de interés).
              Sprint 18: bloqueado si ya hay una conversación activa sobre este activo. */}
          {!isAssetAdmin && (
            <div className="mt-6 border border-[#E2E8F0] rounded-sm p-5 bg-white flex items-center justify-between gap-4 flex-wrap">
              <div>
                <Button
                  onClick={openReuse}
                  disabled={!!thread}
                  className="rounded-sm bg-[#10B981] hover:bg-[#059669] text-white h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={thread ? "Hay una conversación activa sobre este activo. Espera a que se complete o cancele." : undefined}
                  data-testid="action-reuse"
                >
                  <Recycle size={16} className="mr-2" /> Reutilizar
                </Button>
                {thread && (
                  <div className="text-[11px] text-[#5C6A7D] mt-2" data-testid="reuse-blocked-notice">
                    Hay una conversación en curso. Espera a que se complete o cancele para volver a solicitar.
                  </div>
                )}
              </div>
              <div className="text-sm font-semibold text-[#047857]" data-testid="reuse-points-hint">+100 puntos para tu proyecto</div>
            </div>
          )}
          {isAssetAdmin && (
            <div className="mt-6 border border-[#E2E8F0] rounded-sm p-4 bg-[#FAFAFA] text-xs text-[#5C6A7D]" data-testid="asset-admin-block-notice">
              Como administrador de activos, no puedes solicitar reutilizaciones (conflicto de interés con la fijación de precios).
            </div>
          )}
        </div>
      </div>

      {/* Reuse request flow */}
      <Dialog open={reuseOpen} onOpenChange={(o) => !o && setReuseOpen(false)}>
        <DialogContent className="rounded-sm">
          {step === 1 ? (
            <>
              <DialogHeader><DialogTitle className="font-display tracking-tight">¿Cuánto necesitas?</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Cantidad {asset.unit ? `(${unitLabel(qty, asset.unit)})` : ""}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={maxQty}
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    className="rounded-sm h-11"
                    data-testid="reuse-quantity-input"
                  />
                  <p className="text-xs text-[#5C6A7D]">Disponible: {formatQuantity(maxQty, asset.unit)}.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReuseOpen(false)} className="rounded-sm">Cancelar</Button>
                <Button onClick={nextStep} className="bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm" data-testid="reuse-next-step">Continuar</Button>
              </DialogFooter>
            </>
          ) : step === 2 ? (
            <>
              <DialogHeader><DialogTitle className="font-display tracking-tight">¿Hasta cuándo lo vas a requerir?</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Fecha de necesidad</Label>
                  <Input
                    type="date"
                    value={neededUntil}
                    onChange={(e) => setNeededUntil(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    className="rounded-sm h-11"
                    data-testid="reuse-needed-until-input"
                  />
                  <p className="text-xs text-[#5C6A7D]">
                    Al completarse la reutilización, este equipamiento estará asignado a tu proyecto hasta la fecha que indiques.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-sm">Atrás</Button>
                <Button
                  onClick={confirmNeededUntil}
                  disabled={!neededUntil}
                  className="bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm"
                  data-testid="reuse-needed-until-next"
                >
                  Continuar
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader><DialogTitle className="font-display tracking-tight">Mensaje para el equipo</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Mensaje para el equipo que tiene este recurso</Label>
                <Textarea
                  value={reuseMsg}
                  onChange={(e) => setReuseMsg(e.target.value)}
                  placeholder="Hola, pienso pasar por este recurso mañana a las 4:00 p. m. ¿Con quién me comunico?"
                  className="rounded-sm min-h-[120px]"
                  data-testid="reuse-message-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStep(asset.is_surplus ? 1 : 2)} className="rounded-sm">Atrás</Button>
                <Button onClick={sendRequest} disabled={submitting} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-sm" data-testid="send-reuse-request">
                  {submitting ? "Enviando…" : "Enviar solicitud de reutilización"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Team members dialog (asset only) */}
      <Dialog open={teamOpen} onOpenChange={(o) => !o && setTeamOpen(false)}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Equipo de {asset.project_name || "el proyecto"}</DialogTitle>
          </DialogHeader>
          {membersLoading ? (
            <div className="text-sm text-[#5C6A7D] py-4">Cargando…</div>
          ) : members.length === 0 ? (
            <div className="text-sm text-[#5C6A7D] py-4">Este proyecto aún no tiene integrantes registrados.</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-auto -mx-1 px-1">
              {members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-3 border border-[#E2E8F0] rounded-sm" data-testid={`team-member-${m.id}`}>
                  <Avatar name={m.name} photo={m.photo} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#0A2540] truncate">{m.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#5C6A7D]">{m.role_label}</div>
                    {m.email && (
                      <a href={`mailto:${m.email}`} className="text-xs text-[#0A2540] hover:text-[#10B981] inline-flex items-center gap-1 mt-0.5 truncate">
                        <Mail size={12} /> {m.email}
                      </a>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openNotify(m)}
                    className="rounded-sm h-8 text-xs"
                    data-testid={`notify-member-${m.id}`}
                  >
                    <Send size={12} className="mr-1" /> Enviar mensaje
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setTeamOpen(false)} className="rounded-sm">Cerrar</Button>
            {members.length > 0 && (
              <Button
                onClick={() => openNotify("all")}
                className="rounded-sm bg-[#0A2540] hover:bg-[#173B63] text-white"
                data-testid="notify-all-button"
              >
                <Send size={14} className="mr-1.5" /> Enviar a todo el equipo
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Direct notification dialog */}
      <Dialog open={!!notifyTarget} onOpenChange={(o) => !o && setNotifyTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="notify-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {notifyTarget === "all"
                ? `Mensaje al equipo de ${asset.project_name || "el proyecto"}`
                : `Mensaje a ${notifyTarget?.name || ""}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Mensaje</Label>
            <Textarea
              value={notifyMsg}
              onChange={(e) => setNotifyMsg(e.target.value)}
              placeholder="Hola, necesito coordinar algo sobre este activo…"
              className="rounded-sm min-h-[120px]"
              data-testid="notify-message-input"
            />
            <p className="text-xs text-[#5C6A7D]">
              Se enviará como notificación in-app a {notifyTarget === "all" ? "todo el equipo del proyecto" : "esta persona"}.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotifyTarget(null)} className="rounded-sm">Cancelar</Button>
            <Button
              onClick={sendNotify}
              disabled={notifySending}
              className="rounded-sm bg-[#10B981] hover:bg-[#059669] text-white"
              data-testid="notify-send"
            >
              {notifySending ? "Enviando…" : "Enviar mensaje"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
